/**
 * The scrollable body of any overlay that contains a text field.
 *
 * WHY THIS IS SEPARATE FROM `AppModal`
 * -----------------------------------
 * The obvious plan for "modals are unusable with a keyboard" was to migrate
 * every overlay onto `AppModal`. That plan stalls on a real constraint: several
 * overlays are visually bespoke on purpose — `BetaLoginModal` is a dark
 * landing-page surface, `SearchFilters` and the DD panels are hand-polished —
 * and the modal design doc forbids migrations that regress a modal's look
 * ("a modal that gains nothing opts out"). So "adopt the primitive" and "fix the
 * keyboard" were in tension, and the keyboard lost, repeatedly.
 *
 * Splitting the BEHAVIOUR out of the CHROME removes the tension. `AppModal`
 * uses this for its body; a bespoke overlay can use it too and keep every pixel
 * of its own design. The CI ratchet can then require this component around any
 * overlay `TextInput` — a rule every surface can actually satisfy.
 *
 * WHAT IT FIXES
 * -------------
 * A plain `ScrollView` does not move when a field takes focus, so on any form
 * taller than a few rows the lower inputs sit behind the keyboard and you cannot
 * see what you are typing. `KeyboardAwareScrollView` scrolls the focused input
 * into view and keeps clearance above the keyboard.
 *
 * NOT keyboard-controller's `KeyboardAvoidingView`: that collapsed a flex
 * ScrollView to zero height on native in this codebase (UAT 2026-07-21, see
 * `InterviewContainer`). Only the scroll container comes from that library.
 *
 * Web renders a plain `ScrollView` — there is no soft keyboard there, and
 * `KeyboardProvider` is mounted native-only in `src/app/_layout.tsx`.
 */

import React, { forwardRef } from 'react';
import { ScrollView, type ScrollViewProps, type StyleProp, type ViewStyle } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

import { isWeb } from '@/lib/platform';
import { modalTokens } from './modal-tokens';

interface KeyboardAwareBodyProps extends ScrollViewProps {
  children: React.ReactNode;
  contentContainerStyle?: StyleProp<ViewStyle>;
  /** Clearance kept between the focused input and the keyboard. */
  bottomOffset?: number;
}

export const KeyboardAwareBody = forwardRef<ScrollView, KeyboardAwareBodyProps>(
  function KeyboardAwareBody(
    { children, contentContainerStyle, bottomOffset = modalTokens.keyboardBottomOffset, style, ...rest },
    ref
  ) {
    const shared = {
      ref,
      showsVerticalScrollIndicator: false,
      // Lets a tap land on a button while the keyboard is open instead of being
      // swallowed by the dismiss — otherwise "Save" needs two taps.
      keyboardShouldPersistTaps: 'handled' as const,
      style: [{ flexShrink: 1 }, style],
      contentContainerStyle,
      ...rest,
    };

    if (isWeb) {
      return <ScrollView {...shared}>{children}</ScrollView>;
    }

    return (
      <KeyboardAwareScrollView {...shared} bottomOffset={bottomOffset}>
        {children}
      </KeyboardAwareScrollView>
    );
  }
);
